<template>
  <div ref="plotDiv" v-show="isGraphReadyToDraw">
    <v-dialog fullscreen v-model="isFullScreen" eager>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="toggleFullScreen()">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title v-if="this.title">{{ title }} </v-toolbar-title>
        </v-toolbar>
        <div class="dialogContentContainer">
          <div ref="fullscreenPlotDiv" class="fullscreenPlotDiv"></div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Plotly from 'plotly.js-dist'
import { v4 as uuid } from 'uuid'

export default {
  name: 'FullscreenGraphComponent',
  props: {
    maneuversDates: {
      type: Array,
      default: () => [
        {
          legendText: 'Maneuvers Dates', //Text to be displayed in the legend
          color: '#737373', //Color of the lines/recatngles representing the maneuvers. Defaults to this.maneuversColor
          opacity: 0.3, //Opacity of the color for rectangles. Defaults to this.maneuversOpacity
          axis: 'x', //Axis on which to draw the lines/rectangles, either x or y. Defaults to x
          dates: [] // [date1, date2, [startDate3, endDate3], date4]
        }
      ]
    },
    traces: {
      type: Array,
      default: () => null
    },
    layout: {
      type: Object,
      default: () => null
    },
    forceLayout: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => null
    },
    title: { type: String, default: '' }
  },
  mixins: [],
  data() {
    return {
      isFullScreen: false,
      id: uuid(),
      resetResizeObserver: null,
      maneuversColor: '#737373',
      maneuversOpacity: 0.3,
      showManeuvers: true,
      hiddenManeuversNames: []
    }
  },

  watch: {
    traces: {
      handler() {
        this.updateGraph()
      }
    },
    layout: {
      handler() {
        this.updateGraph()
      }
    },
    config: {
      handler() {
        this.updateGraph()
      }
    },
    hiddenManeuversNames() {
      this.updateGraph()
    },
    isFullScreen() {
      this.updateGraph()
    },
    maneuversDates(newValue) {
      this.updateGraph()
    },
    isGraphReadyToDraw: {
      handler: async function (newValue) {
        if (newValue === true) {
          await this.$nextTick()
          this.updateGraph()
          if (this.resetResizeObserver != null) {
            this.resetResizeObserver()
          }
          const elem = this.$refs.plotDiv
          const resizeObserver = new ResizeObserver(() => {
            Plotly.Plots.resize(elem).catch(() => {
              //The resize may fail if the element is no longer displayed, but it is not a problem
            })
          })
          resizeObserver.observe(elem)
          this.resetResizeObserver = function () {
            resizeObserver.unobserve(elem)
          }
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.resetResizeObserver != null) {
      this.resetResizeObserver()
    }
    if (this.resetLegendListener != null) {
      this.resetLegendListener()
    }
  },
  computed: {
    isGraphReadyToDraw() {
      return (
        this.traces != null &&
        this.plotLayout != null &&
        this.plotConfig != null
      )
    },
    plotConfig() {
      if (this.config == null) {
        return {
          responsive: true,
          modeBarButtonsToRemove: ['select2d', 'lasso2d'],
          modeBarButtonsToAdd: [this.fullscreenButton]
        }
      } else {
        return this.config
      }
    },
    baseLayout() {
      if (this.forceLayout) {
        return {}
      }
      return {
        plot_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        paper_bgcolor: 'transparent',
        font: { color: this.$vuetify.theme.dark ? 'white' : '#121212' },
        legend: {
          x: 1,
          xanchor: 'right',
          y: 1,
          orientation: 'h'
        },
        margin: { t: 20 }
      }
    },
    plotLayout() {
      return {
        ...this.baseLayout,
        ...this.layout,
        shapes: [...(this.layout?.shapes || []), ...this.maneuverHighlight]
      }
    },
    plotTraces() {
      return [...this.traces, ...this.maneuversLegend]
    },

    fullscreenButton() {
      return {
        name: 'Full screen',
        icon: Plotly.Icons.pan,
        direction: 'up',
        click: this.toggleFullScreen
      }
    },
    nonEmptyManeuversDates() {
      return this.maneuversDates == null
        ? []
        : this.maneuversDates.filter((maneuverDates) =>
            Boolean(maneuverDates.dates?.length)
          )
    },
    displayedManeuverDates() {
      return this.nonEmptyManeuversDates.filter(
        (maneuverDates) =>
          !this.hiddenManeuversNames.includes(maneuverDates.legendText)
      )
    },
    maneuversLegend() {
      return this.nonEmptyManeuversDates.map((maneuverDates, index) => {
        if (
          maneuverDates.dates.findIndex((dates) => Array.isArray(dates)) !== -1
        ) {
          return {
            x: [null],
            y: [null],
            type: 'bar',
            marker: {
              color: maneuverDates.color || this.maneuversColor,
              line: { color: 'transparent' }
            },
            opacity: maneuverDates.opacity || this.maneuversOpacity,
            name: maneuverDates.legendText || `Maneuvers ${index}`,
            showlegend: true
          }
        } else {
          return {
            x: [null],
            y: [null],
            type: 'scatter',
            mode: 'markers',
            marker: {
              color: maneuverDates.color || this.maneuversColor,
              size: 20,
              symbol: 'line-ns',
              line: {
                color: maneuverDates.color || this.maneuversColor,
                width: 2
              }
            },
            opacity: maneuverDates.opacity || this.maneuversOpacity,
            name: maneuverDates.legendText || `Maneuvers ${index}`,
            showLegend: true
          }
        }
      })
    },
    maneuverHighlight() {
      const shape = []
      for (const maneuverDates of this.displayedManeuverDates) {
        const isDatesOnYAxis = maneuverDates.axis === 'y'
        for (const dates of maneuverDates.dates) {
          if (Array.isArray(dates)) {
            const initialDate = dates[0]
            const finalDate = dates[dates.length - 1]
            const rectangle = {
              type: 'rect',

              xref: isDatesOnYAxis ? 'paper' : 'x',
              x0: isDatesOnYAxis ? 0 : initialDate,
              x1: isDatesOnYAxis ? 1 : finalDate,

              yref: isDatesOnYAxis ? 'y' : 'paper',
              y0: isDatesOnYAxis ? initialDate : 0,
              y1: isDatesOnYAxis ? finalDate : 1,

              fillcolor: maneuverDates.color || this.maneuversColor,
              opacity: maneuverDates.opacity || this.maneuversOpacity,
              line: {
                width: 0
              }
            }
            shape.push(rectangle)
          } else if (typeof dates == 'string') {
            const line = {
              type: 'line',

              xref: isDatesOnYAxis ? 'paper' : 'x',
              x0: isDatesOnYAxis ? 0 : dates,
              x1: isDatesOnYAxis ? 1 : dates,

              yref: isDatesOnYAxis ? 'y' : 'paper',
              y0: isDatesOnYAxis ? dates : 0,
              y1: isDatesOnYAxis ? dates : 1,

              line: {
                color: maneuverDates.color || this.maneuversColor,
                width: 1
              }
            }
            shape.push(line)
          }
        }
      }
      return shape
    }
  },
  methods: {
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen
    },
    initLegendListener(plotDiv) {
      const callback = (data) => {
        this.$emit('legendClick', data)
        const maneuverName = data.data[data.curveNumber].name
        if (this.hiddenManeuversNames.includes(maneuverName)) {
          this.hiddenManeuversNames = this.hiddenManeuversNames.filter(
            (name) => name != maneuverName
          )
        } else {
          this.hiddenManeuversNames = [
            ...this.hiddenManeuversNames,
            maneuverName
          ]
        }
      }
      if (this.resetLegendListener != null) {
        this.resetLegendListener()
      }
      this.resetLegendListener = () => {
        plotDiv.removeListener('plotly_legendclick', callback)
      }
      plotDiv.on('plotly_legendclick', callback)
    },
    async drawGraph() {
      await this.$nextTick()
      const plotDiv = this.isFullScreen
        ? this.$refs.fullscreenPlotDiv
        : this.$refs.plotDiv
      if (plotDiv) {
        Plotly.react(plotDiv, this.plotTraces, this.plotLayout, this.plotConfig)
        Plotly.Plots.resize(plotDiv)
        this.initLegendListener(plotDiv)
      }
    },
    updateGraph() {
      if (this.isGraphReadyToDraw) {
        this.drawGraph()
      }
    }
  }
}
</script>

<style scoped>
.fullscreenPlotDiv {
  width: 100%;
  height: 100%;
}

.dialogContentContainer {
  padding: 30px;
  height: calc(100vh - 70px);
}
</style>
